import { Link, useParams } from "react-router-dom";
import type { UserProfile } from "@shared/types";
import { wareraUserUrl } from "@shared/warera-links";
import { candidacyPath, candidateShareUrl } from "@shared/share-links";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { Page, Card, RichLoader, ErrorBox } from "../components/ui";
import { useDelayedFlag } from "../lib/useDelayedFlag";
import { useWaitMessage } from "../lib/useWaitMessage";
import { useT } from "../i18n";
import { usePageTitle } from "../lib/usePageTitle";
import { Stat } from "../components/Stat";
import { fmtDmg, dateLabel, sinceLabel, agoLabel } from "../lib/format";
import { ClaimBadge } from "../components/StatusBadge";
import { Share } from "../components/Share";

/** /u/:username — any WarEra player's profile: live stats, their election
 * history (with results), and their campaigns on WarUrna. Campaign pages live
 * under /u/:username/c/:candidacyId. */
export default function Profile() {
  const t = useT();
  const { username = "" } = useParams();
  const { data: p, loading, error } = useAsync(() => api.userProfile(username), [username]);
  const showRich = useDelayedFlag(loading, 400);
  const waitKey = useWaitMessage(loading);
  usePageTitle(p ? t("profile.shareTitle", { name: p.username }) : null);

  return (
    <Page>
      {showRich && <RichLoader headline={t("profile.loading", { name: username || "…" })} message={t(waitKey)} />}
      {error && <ErrorBox msg={error} />}
      {p && (
        <>
          <div className="flex flex-wrap items-center justify-between my-3 gap-2">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold truncate">{p.username}</h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={wareraUserUrl(p.userId)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-accent border border-border rounded-full px-3 py-1 hover:border-accent"
              >
                {t("profile.viewOnWarera")} ↗
              </a>
              <Share
                title={t("profile.shareTitle", { name: p.username })}
                text={t("profile.shareText", { name: p.username })}
                url={candidateShareUrl(p.username)}
              />
              {p.claimStatus && <ClaimBadge claimStatus={p.claimStatus} />}
            </div>
          </div>

          <StatsCard p={p} />
          <CurrentCampaign p={p} />
          <ElectionHistory p={p} />
          <PastCandidacies p={p} />
        </>
      )}
    </Page>
  );
}

/** The newest candidacy whose race isn't decided yet — "current" by the same
 * data the history table shows (no linked election = race still ahead). */
function currentCampaignOf(p: UserProfile) {
  return p.candidacies.find((cd) => {
    const e = p.elections.find((x) => x.candidacyId === cd.id);
    return !e || e.phase !== "done";
  });
}

/** Same stat grid as the campaign page, fed by the profile payload. */
function StatsCard({ p }: { p: UserProfile }) {
  const t = useT();
  const s = p.stats;
  return (
    <Card className="mb-3">
      <div className="text-[11px] uppercase tracking-wide text-muted mb-3">{t("profile.playerStats")}</div>
      {s ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
          <Stat label={t("stats.level")} value={s.level} />
          <Stat label={t("stats.careerDmg")} value={fmtDmg(s.careerDamage)} sub={s.careerDamageRank ? `#${s.careerDamageRank}` : undefined} />
          <Stat label={t("stats.militaryRank")} value={`#${s.militaryRank}`} />
          <Stat label={t("stats.wealth")} value={fmtDmg(s.wealth)} />
          <Stat label={t("stats.premium")} value={s.premiumMonths ? `${s.premiumMonths}mo` : "—"} />
          <Stat label={t("stats.subscribers")} value={s.subscribers} />
          <Stat label={t("stats.referrals")} value={s.referrals} />
          <Stat label={t("stats.playingSince")} value={sinceLabel(s.createdAt)} />
          <Stat label={t("stats.lastActive")} value={agoLabel(s.lastConnectionAt, t)} />
        </div>
      ) : (
        <p className="text-muted text-sm">{t("profile.statsPending")}</p>
      )}
    </Card>
  );
}

function CurrentCampaign({ p }: { p: UserProfile }) {
  const t = useT();
  const cd = currentCampaignOf(p);
  if (!cd) return null;
  return (
    <Card className="mb-3">
      <div className="text-[11px] uppercase tracking-wide text-muted mb-2">{t("profile.currentCampaign")}</div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 text-sm">
          <span className="font-semibold capitalize">
            {cd.office === "president" ? t("office.president") : t("office.congress")}
          </span>
          {" · "}
          <span>{cd.countryName ?? cd.countryId}</span>
          {" · "}
          <span>{dateLabel(cd.cycleStartAt)}</span>
          <div className="text-muted mt-0.5">{cd.party || t("common.independent")}</div>
        </div>
        <Link to={candidacyPath(p.username, cd.id)} className="text-accent text-sm shrink-0">
          {t("profile.viewCampaign")} →
        </Link>
      </div>
    </Card>
  );
}

function ElectionHistory({ p }: { p: UserProfile }) {
  const t = useT();
  return (
    <Card className="mb-3">
      <div className="text-[11px] uppercase tracking-wide text-muted mb-2">{t("profile.electionHistory")}</div>
      {p.elections.length === 0 ? (
        <p className="text-muted text-sm">{t("profile.noElections")}</p>
      ) : (
        <div className="divide-y divide-border">
          {p.elections.map((el) => (
            <div key={el.electionId} className="py-2 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <Link to={`/c/${el.countryId}/e/${el.electionId}`} className="min-w-0 hover:text-accent">
                  <span className="font-semibold capitalize">
                    {el.type === "president" ? t("office.president") : t("office.congress")}
                  </span>
                  {" · "}
                  <span>{el.countryName ?? el.countryId}</span>
                  {" · "}
                  <span className="text-muted">{dateLabel(Date.parse(el.endAt))}</span>
                </Link>
                {el.phase === "done" && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${
                      el.elected ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10" : "border-border text-muted"
                    }`}
                  >
                    {el.elected ? t("profile.elected") : t("profile.notElected")}
                  </span>
                )}
              </div>
              <div className="text-xs text-muted mt-0.5 flex flex-wrap gap-x-3">
                <span>{t("profile.votes", { count: el.voteCount })}</span>
                <span>{t("profile.rankOf", { rank: el.rank, total: el.totalCandidates })}</span>
                {el.seats > 1 && <span>{t("profile.seats", { count: el.seats })}</span>}
                {el.candidacyId && (
                  <Link to={candidacyPath(p.username, el.candidacyId)} className="text-accent">
                    {t("profile.viewCampaign")} →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function PastCandidacies({ p }: { p: UserProfile }) {
  const t = useT();
  const current = currentCampaignOf(p);
  const past = p.candidacies.filter((cd) => cd.id !== current?.id);
  if (p.candidacies.length === 0) {
    return (
      <Card className="mb-3">
        <div className="text-[11px] uppercase tracking-wide text-muted mb-2">{t("profile.pastCandidacies")}</div>
        <p className="text-muted text-sm">{t("profile.noCandidacies")}</p>
      </Card>
    );
  }
  if (past.length === 0) return null;
  return (
    <Card className="mb-3">
      <div className="text-[11px] uppercase tracking-wide text-muted mb-2">{t("profile.pastCandidacies")}</div>
      <div className="divide-y divide-border">
        {past.map((cd) => (
          <Link key={cd.id} to={candidacyPath(p.username, cd.id)} className="block py-2 first:pt-0 last:pb-0 hover:text-accent">
            <div className="text-sm">
              <span className="font-semibold capitalize">
                {cd.office === "president" ? t("office.president") : t("office.congress")}
              </span>
              {" · "}
              <span>{cd.countryName ?? cd.countryId}</span>
              {" · "}
              <span className="text-muted">{dateLabel(cd.cycleStartAt)}</span>
            </div>
            <div className="text-xs text-muted mt-0.5">{cd.party || t("common.independent")}</div>
          </Link>
        ))}
      </div>
    </Card>
  );
}
